// Fatia S1: membros da conta, acessos por empresa, convites e exigência de 2FA.
import { useQueryClient } from '@tanstack/react-query'
import { ShieldCheck } from 'lucide-react'
import type { AccountRole, MeAccount } from '@impulso/shared'
import { api, errorMessage } from '../../lib/api'
import { useSession } from '../../lib/session'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import { ErrorState, Loading, PageHead, Switch } from '../../ui'
import AccountGate from '../../features/account/AccountGate'
import InviteForm from '../../features/account/InviteForm'
import { useAccount, useMembers } from '../../features/account/api'
import { ACCOUNT_ROLE_LABEL, COMPANY_ROLE_LABEL, dateBR, initials } from '../../features/account/labels'

export default function Users() {
  return <AccountGate permission="account.manage_members">{(account) => <UsersInner account={account} />}</AccountGate>
}

function UsersInner({ account }: { account: MeAccount }) {
  const { me, refresh } = useSession()
  const q = useMembers(account.id)
  const acc = useAccount(account.id)
  const qc = useQueryClient()
  const toast = useToast()
  const confirm = useConfirm()
  const isOwner = account.role === 'owner'
  const reload = () => qc.invalidateQueries({ queryKey: ['members', account.id] })

  async function act(fn: () => Promise<unknown>, msg: string) {
    try { await fn(); toast(msg); await reload(); await refresh() } catch (err) { toast(errorMessage(err)) }
  }
  const changeRole = (userId: string, role: AccountRole) => act(() => api.patch(`/accounts/${account.id}/members/${userId}`, { role }), 'Papel atualizado.')
  const remove = async (userId: string, name: string) => {
    if (!(await confirm({ title: `Remover ${name} da conta?`, message: 'A pessoa perde o acesso a todas as empresas.', confirmLabel: 'Remover', danger: true }))) return
    void act(() => api.del(`/accounts/${account.id}/members/${userId}`), `${name} foi removido(a).`)
  }
  const revoke = (id: string) => act(() => api.del(`/accounts/${account.id}/invitations/${id}`), 'Convite cancelado.')
  const toggle2fa = (v: boolean) => act(async () => { await api.patch(`/accounts/${account.id}`, { require2fa: v }); await qc.invalidateQueries({ queryKey: ['account', account.id] }) }, v ? 'Agora todos precisam da verificação em duas etapas.' : 'Exigência desligada.')

  return (
    <div className="stack" style={{ gap: 20 }}>
      <PageHead title="Usuários" sub={q.data ? `${q.data.usage.users} de ${q.data.maxUsers >= 500 ? 'ilimitados' : q.data.maxUsers} usuários do plano (clientes do portal não contam)` : undefined} />
      <div className="grid g-main">
        <div className="card">
          <h2>Pessoas com acesso</h2>
          <div className="list mt-16">
            {q.isLoading && <Loading lines={3} />}
            {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
            {q.data?.items.map((p) => {
              const self = p.userId === me?.user.id
              const lockOwner = p.accountRole === 'owner' && !isOwner
              return (
                <div key={p.userId} className="list-item" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="avatar sm">{initials(p.name)}</span>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div className="strong">{p.name}{self && <span className="muted"> (você)</span>} {p.totpEnabled && <ShieldCheck size={13} color="var(--green)" aria-label="2FA ativado" />}</div>
                    <div className="small muted" style={{ overflowWrap: 'anywhere' }}>{p.email}</div>
                    {p.grants.length > 0 && <div className="small muted">{p.grants.map((g) => `${g.companyName} (${COMPANY_ROLE_LABEL[g.role]})`).join(' · ')}</div>}
                  </div>
                  <div className="row" style={{ gap: 8 }}>
                    <select className="select" style={{ width: 'auto' }} aria-label={`Papel de ${p.name}`} value={p.accountRole ?? ''} disabled={lockOwner || (self && p.accountRole === 'owner' && !isOwner)}
                      onChange={(e) => e.target.value && changeRole(p.userId, e.target.value as AccountRole)}>
                      {!p.accountRole && <option value="">Só empresas</option>}
                      {(['owner', 'admin', 'member'] as AccountRole[]).filter((r) => isOwner || r !== 'owner' || p.accountRole === 'owner').map((r) => <option key={r} value={r}>{ACCOUNT_ROLE_LABEL[r]}</option>)}
                    </select>
                    {!self && !lockOwner && <button className="btn sm ghost" onClick={() => remove(p.userId, p.name)}>Remover</button>}
                  </div>
                </div>
              )
            })}
          </div>
          {q.data && q.data.invitations.length > 0 && (
            <>
              <div className="divider" />
              <h3>Convites pendentes</h3>
              <div className="list mt-12">
                {q.data.invitations.map((i) => (
                  <div key={i.id} className="list-item" style={{ alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="strong" style={{ overflowWrap: 'anywhere' }}>{i.email}</div>
                      <div className="small muted">
                        {i.companyName ? `${i.companyName} · ${i.companyRole ? COMPANY_ROLE_LABEL[i.companyRole] : ''}` : i.accountRole ? ACCOUNT_ROLE_LABEL[i.accountRole] : ''}
                        {' · '}{i.expired ? 'expirou' : `vale até ${dateBR(i.expiresAt)}`}
                      </div>
                    </div>
                    <button className="btn sm ghost" onClick={() => revoke(i.id)}>Cancelar</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="stack">
          <div className="card">
            <h2>Convidar para a conta</h2>
            <p className="small muted mt-8">Para dar acesso a uma única empresa, use Configurações → Equipe dentro da empresa.</p>
            <div className="mt-16"><InviteForm mode="account" accountId={account.id} isOwner={isOwner} canBill={account.permissions.includes('account.billing')} onDone={reload} /></div>
          </div>
          <div className="card">
            <div className="row between" style={{ alignItems: 'flex-start' }}>
              <div>
                <h3>Exigir verificação em duas etapas</h3>
                <p className="small muted mt-8">Todos da conta precisarão ativar o código do celular para entrar. Recomendado para agências.</p>
              </div>
              {acc.data ? <Switch on={acc.data.require2fa} label="Exigir verificação em duas etapas" onChange={toggle2fa} /> : <Loading lines={1} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
